import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { LLM, LLMID, ModelProvider } from "@/types/base"
import { cache } from 'react'
import { getAidonProvider } from './providers'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { PermAction, PermOptions, permOptionsKey, WithPermissions } from '@/types/base/permissions'
import Authuser, { authCanPerform, evaluatePermissions } from '@/types/base/authuser'
import { getWorkspaceById } from './workspaces'

const logger = createIsomorphicLogger('db.models')

export type ModelRecordWithProviderName = Tables<"models"> & {
  provider_name: string;
}

export type ModelRecordWithProviderNameAndPermissions = WithPermissions<ModelRecordWithProviderName>

type WorkspaceRecordWithProviderName = {
  id: string;
  name: string;
  models: ModelRecordWithProviderName[];
}

export const getModelById = async (supabase: SupabaseClient<Database>, modelId: string): Promise<ModelRecordWithProviderName> => {
  const { data: model, error } = await supabase
    .from("models")
    .select(`
      *,
      providers!left(internal_name)
    `)
    .eq("id", modelId)
    .single()

  if (!model) {
    logError(logger, error, 'Failed to fetch Model by ID', { modelId })
    throw new Error(error.message)
  }

	const { providers, ...rest } = model as any;
	return {
		...rest,
		provider_name: providers ? providers.internal_name : "custom", // Use "custom" if internal_name is null or undefined
	} as ModelRecordWithProviderName
}

export const getOptionalModelById = async (supabase: SupabaseClient<Database>, modelId: string) => {
  const { data: model, error } = await supabase
    .from("models")
    .select("*")
    .eq("id", modelId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Model by ID', { modelId })
    throw new Error(error.message)
  }

  return model
}

export const getOptionalModelByModelId = async (supabase: SupabaseClient<Database>, modelId: string) => {
  const { data: model, error } = await supabase
    .from("models")
    .select("*")
    .eq("model_id", modelId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Model by Model ID', { modelId })
    throw new Error(error.message)
  }

  return model
}

export const getOptionalModelWithProviderNameByModelId = async (supabase: SupabaseClient<Database>, modelId: string) => {
  const { data: model, error } = await supabase
    .from("models")
    .select(`
      *,
      providers!left(internal_name)
    `)
    .eq("model_id", modelId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Model with provider name by Model ID', { modelId })
    throw new Error(error.message)
  }

	if (model) {
		const { providers, ...rest } = model as any;
		return {
			...rest,
			provider_name: providers ? providers.internal_name : "custom", // Use "custom" if internal_name is null or undefined
		} as ModelRecordWithProviderName
	}

  return null
}

export const getOptionalWorkspaceModelByModelId = async (
	supabase: SupabaseClient<Database>,
	workspaceId: string,
	modelId: string
) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
    `
      models (*)
    `
    )
    .eq("id", workspaceId)
    .eq("models.model_id", modelId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Workspace Model by id', { modelId, workspaceId })
    throw new Error(error.message)
  }

  return data ? data.models[0] : null
}

export const getOptionalModelByName = async (supabase: SupabaseClient<Database>, modelName: string,
		workspaceId: string) => {
  const { data, error } = await supabase
    .from("workspaces")
    .select(
      `
      models (*)
    `
    )
    .eq("id", workspaceId)
    .eq("models.name", modelName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Model by name', { modelName, workspaceId })
    throw new Error(error.message)
  }

  return data ? data.models[0] : null
}

export const getWorkspaceModelsByWorkspaceId = async (
	supabase: SupabaseClient<Database>,
	workspaceId: string
) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      models (
				*,
        providers!left(internal_name)
			)
    `
    )
    .eq("id", workspaceId)
		.is("models.provider_id", null)
    .single()

  if (!workspace) {
    logError(logger, error, 'Failed to fetch Models by workspace ID', { workspaceId })
    throw new Error(error.message)
  }

	const workspaceWithProviderName = {
    ...workspace,
    models: workspace.models.map((model) => {
      const { providers, ...rest } = model as any;
      return {
        ...rest,
        provider_name: providers ? providers.internal_name : "custom", // Use "custom" if internal_name is null or undefined
      };
    }),
  } as WorkspaceRecordWithProviderName;

  return workspaceWithProviderName;
}

export const getWorkspaceModelsByWorkspaceTenantId = async (
	supabase: SupabaseClient<Database>,
	workspaceId: string,
	tenantId: string
) => {
  const { data: workspace, error } = await supabase
    .from("workspaces")
    .select(
      `
      id,
      name,
      models (
				*,
        providers!left(internal_name)
			)
    	`
    )
    .eq("id", workspaceId)
    .single()

  if (!workspace) {
    logError(logger, error, 'Failed to fetch Models by workspace tenant ID', { workspaceId, tenantId })
    throw new Error(error.message)
  }

	const aidonProvider = await getAidonProvider(supabase)
	const filteredModels = workspace.models.filter(model => model.provider_id === aidonProvider?.id)

	const modelsWithProviderName: ModelRecordWithProviderName[] = filteredModels.map((model: any) => {
    const { providers, ...rest } = model
    return {
      ...rest,
      provider_name: providers ? providers.internal_name : "aidon"
    }
  })

  return modelsWithProviderName
}

export const getWorkspaceModelsByWorkspaceTenantIdWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string,
	tenantId: string
) : Promise<ModelRecordWithProviderNameAndPermissions[]> => {
	const wsModels = await getWorkspaceModelsByWorkspaceTenantId(supabase, workspaceId, tenantId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: ModelRecordWithProviderNameAndPermissions[] = []

	for (const [i, model] of wsModels.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "models", context: "ANY", uuid: model.id, tenant: model.owning_tenant, ws: workspace})

		const options = { type: "models", action: PermAction.view, context: "provider_id", uuid: model.id,
				tenant:model.owning_tenant, ws: workspace } as PermOptions
		const allowed = await authCanPerform(supabase, authuser, options)
		const viewProviderKey = permOptionsKey(options)
		perms[viewProviderKey] = allowed

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "models", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...model, permissions: perms })
	}

  return result
}

export const getModelWorkspacesByModelId = async (supabase: SupabaseClient<Database>, modelId: string) => {
  const { data: model, error } = await supabase
    .from("models")
    .select(
      `
      id,
      name,
			provider_id,
      workspaces (*)
    `
    )
    .eq("id", modelId)
    .single()

  if (!model) {
    logError(logger, error, 'Failed to fetch Model workspaces by Model ID', { modelId })
    throw new Error(error.message)
  }

  return model
}

export const getModelsByProviderId = async (supabase: SupabaseClient<Database>, providerId: string) => {
  const { data: models, error } = await supabase
    .from("models")
    .select(`
      *,
      providers!left(internal_name)
    `)
    .eq("provider_id", providerId)

  if (error) {
    logError(logger, error, 'Failed to fetch Models by provider ID', { providerId })
    throw new Error(error.message)
  }

	// Map the results to include provider_name
  const modelsWithProviderName = models.map((model) => {
    const { providers, ...rest } = model as any;
    return {
      ...rest,
      provider_name: providers ? providers.internal_name : "custom", // Use "custom" if internal_name is null or undefined
    }
  }) as ModelRecordWithProviderName[]

  return modelsWithProviderName
}

export const getAllModels = async (supabase: SupabaseClient<Database>) => {
  const { data: models, error } = await supabase
    .from("models")
    .select("*")

  if (!models) {
    logError(logger, error, 'Failed to fetch all Models')
    throw new Error(error.message)
  }

  return models
}

export const getAllProviderModels = async (supabase: SupabaseClient<Database>) => {
  const { data: models, error } = await supabase
    .from("models")
		.select(`
      *,
      providers!left(internal_name)
    `)
		.not("provider_id", "is", null)

	if (!models) {
    logError(logger, error, 'Failed to fetch all provider Models')
    throw new Error(error.message)
  }

	// Map the results to include provider_name
  const modelsWithProviderName = models
			.filter(model => model.providers?.internal_name !== 'aidon')
			.map((model) => {
    		const { providers, ...rest } = model as any;
    		return {
      		...rest,
      		provider_name: providers ? providers.internal_name : "custom", // Use "custom" if internal_name is null or undefined
    		}
  		}) as ModelRecordWithProviderName[]

  return modelsWithProviderName
}

export const getAllProviderModelsWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<ModelRecordWithProviderNameAndPermissions[]> => {
	const allModels = await getAllProviderModels(supabase)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: ModelRecordWithProviderNameAndPermissions[] = []

	for (const [i, model] of allModels.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: "models", context: "ANY", uuid: model.id, tenant: model.owning_tenant, ws: workspace})

		const options = { type: "models", action: PermAction.view, context: "provider_id", uuid: model.id,
				tenant:model.owning_tenant, ws: workspace } as PermOptions
		const allowed = await authCanPerform(supabase, authuser, options)
		const viewProviderKey = permOptionsKey(options)
		perms[viewProviderKey] = allowed

		if (i === 0) { // Attach special non specific permission to first record only
			const options = { type: "models", action: PermAction.create, context: "aidonAdmin" } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const shareKey = permOptionsKey(options)
			perms[shareKey] = allowed
		}

		result.push({ ...model, permissions: perms })
	}

  return result
}

export const getProviderModel = cache(async (supabase: SupabaseClient<Database>, modelId:string) => {
	const allProviders = await getAllProviderModels(supabase)
	const provider: ModelProvider = (allProviders.find(
			provider => provider.model_id === modelId)?.provider_name || "custom") as ModelProvider
	return provider
})

export const getAutoShareModels = async (supabase: SupabaseClient<Database>) => {
  const { data: models, error } = await supabase
    .from("models")
    .select("*")
    .eq("auto_share", true)

  if (error) {
    logError(logger, error, 'Failed to fetch auto-shared Models')
    throw new Error(error.message)
  }

  return models
}

export const createModel = async (
	supabase: SupabaseClient<Database>,
  model: TablesInsert<"models">,
  workspace: Tables<"workspaces">
) => {
	const modelWithTenant = {
		...model,
		owning_tenant: workspace.tenant_id
	}

  const { data: createdModel, error } = await supabase
    .from("models")
    .insert([modelWithTenant])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Model', { model: printObject(model) })
    throw new Error(error.message)
  }

  await createModelWorkspace(supabase, {
    user_id: model.user_id,
    model_id: createdModel.id,
    workspace_id: workspace.id
  })

	const createdModelWithProviderName = {
		...createdModel,
		provider_name: 'custom', // Add the provider_name property with the value "custom"
	}
  logger.info('Model created successfully', { model: printObject(createdModelWithProviderName) })
  return createdModelWithProviderName
}

export const createModels = async (
	supabase: SupabaseClient<Database>,
  models: TablesInsert<"models">[],
  workspace: Tables<"workspaces">
) => {
	const modelsWithTenant = models.map(model => ({
		...model,
		owning_tenant: workspace.tenant_id
	}))

  const { data: createdModels, error } = await supabase
    .from("models")
    .insert(modelsWithTenant)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Models', { models: printObject(models) })
    throw new Error(error.message)
  }

  await createModelWorkspaces(supabase,
    createdModels.map(model => ({
      user_id: model.user_id,
      model_id: model.id,
	    workspace_id: workspace.id
    }))
  )

  logger.info('Models created successfully', { models: printObject(createdModels) })
  return createdModels
}

export const createModelWorkspace = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  model_id: string
  workspace_id: string
}) => {
  const { data: createdModelWorkspace, error } = await supabase
    .from("model_workspaces")
    .insert([item])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Model workspace', { item: printObject(item) })
    throw new Error(error.message)
  }

  logger.info(`Successfully created Model workspaces record`)
  return createdModelWorkspace
}

export const createModelWorkspaces = async (
	supabase: SupabaseClient<Database>,
  items: { user_id: string; model_id: string; workspace_id: string }[]
) => {
  const { data: createdModelWorkspaces, error } = await supabase
    .from("model_workspaces")
    .upsert(items, { onConflict: 'model_id, workspace_id' })
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Model workspaces', { items: printObject(items) })
    throw new Error(error.message)
  }

  logger.info(`Successfully created ${createdModelWorkspaces.length} Model workspaces records`)
  return createdModelWorkspaces
}

export const updateModel = async (
	supabase: SupabaseClient<Database>,
  modelId: string,
  model: TablesUpdate<"models">
) => {
  const { data: updatedModel, error } = await supabase
    .from("models")
    .update(model)
    .eq("id", modelId)
		.select(`
      *,
      providers!left(internal_name)
    `)
		.single()

  if (error) {
    logError(logger, error, 'Failed to update Model', { modelId, model: printObject(model) })
    throw new Error(error.message)
  }

	// Ensure updatedModel has the correct type
// Ensure updatedModel has the correct type
  const updatedModelWithProviders = updatedModel as unknown as Tables<"models"> & {
    providers: { internal_name: string } | null
  }

  // Map the results to include provider_name and exclude the nested providers object
	const updatedModelWithProviderName = {
    ...updatedModelWithProviders,
    provider_name: updatedModelWithProviders.providers ? updatedModelWithProviders.providers.internal_name : "custom", // Use "custom" if internal_name is null or undefined
  }

  // Remove the nested providers object
  const { providers, ...finalUpdatedModel } = updatedModelWithProviderName;

  logger.info('Model updated successfully', { model: printObject(finalUpdatedModel) })
  return finalUpdatedModel
}

export const deleteModel = async (supabase: SupabaseClient<Database>, modelId: string) => {
  const { error } = await supabase.from("models").delete().eq("id", modelId)

  if (error) {
    logError(logger, error, 'Failed to delete Model', { modelId })
    throw new Error(error.message)
  }

  return true
}

export function mapToLLM(record: ModelRecordWithProviderName): LLM {
  return {
    modelId: record.model_id as LLMID,
    modelName: record.name,
		active: !(record.removed || record.deprecated),
    provider: record.provider_name as ModelProvider,
    hostedId: record.id,
    platformLink: record.base_url || '', // Assuming base_url is the platform link
    imageInput: record.supports_images,
    toolInput: record.supports_tools,
		reasoning: record.supports_reasoning,
		maxContext: record.context_length,
		maxOutputToken: record.token_output_length,
		minTemperature: record.min_temperature,
		maxTemperature: record.max_temperature,
    pricing: {
      currency: record.pricing_currency,
      unit: record.pricing_unit,
      inputCost: record.pricing_input,
      outputCost: record.pricing_output,
      image: record.pricing_image,
      request: record.pricing_request,
			cacheRead: record.pricing_cache_read,
			cacheWrite: record.pricing_cache_write,
			reasoning: record.pricing_reasoning,
			webSearch: record.pricing_web_search,
    },
  };
}

export const deleteModelWorkspace = async (
	supabase: SupabaseClient<Database>,
  modelId: string,
  workspaceId: string
) => {
  const { error } = await supabase
    .from("model_workspaces")
    .delete()
    .eq("model_id", modelId)
    .eq("workspace_id", workspaceId)

  if (error) {
    logError(logger, error, 'Failed to delete Model workspace', { modelId, workspaceId })
    throw new Error(error.message)
  }

  return true
}
